import React from "react";
import PropTypes from "prop-types";
import convert from "convert-units";
import _ from "lodash";

import GraphComponent from "../../calendar/readiness/GraphComponent";
import { RPEList } from "../../calendar/session-rpe/RPEList";
import {
  getLabels,
  readyChartData,
  mapUtcToDatePicker,
  filterUnsetValues,
  sortStatsByDate,
} from "../../../../utils/daily-stats";

const UNITS = {
  metric: "KG",
  imperial: "LB",
};

class BodyweightSummary extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.field = "bodyWeight";
    this.precision = 1;
    this.state = {
      statsData: [],
      hoveredDate: "",
      unitSelected: UNITS[context.locals.activeUser.units],
    };

    this.updateHoveredStat = this.updateHoveredStat.bind(this);
  }

  componentDidMount() {
    const { unitSelected } = this.state;
    const stats = filterUnsetValues(this.props.stats, this.field);

    const convertedList =
      unitSelected === "LB" ? this.convertToLb(stats, this.field) : stats;

    const sortedStats = sortStatsByDate(convertedList);
    this.setState({
      statsData: sortedStats,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { unitSelected } = this.state;
    if (!_.isEqual(prevProps.stats, this.props.stats)) {
      console.log("did update");
      const stats = filterUnsetValues(this.props.stats, this.field);

      const convertedList =
        unitSelected === "LB" ? this.convertToLb(stats, this.field) : stats;

      this.setState({
        statsData: convertedList,
      });
    }
  }

  updateHoveredStat(date = "") {
    this.setState({ hoveredDate: date });
  }

  convertToLb(stats, field) {
    const statsCopy = _.cloneDeep(stats);
    const lbsArray = statsCopy.map((stat) => {
      const lbsValue = convert(stat[field]).from("kg").to("lb");
      stat[field] = lbsValue;
      return stat;
    });
    return lbsArray;
  }

  roundStats(stats, field, precision) {
    return stats.map((stat) => {
      stat[field] = _.round(stat[field], precision);
      return stat;
    });
  }

  calculateAvg(stats, field, precision) {
    const total = stats
      .map((stat) => stat[field])
      .reduce((prev, current) => prev + current, 0);

    const avg = _.round(total / stats.length, precision);
    return avg;
  }

  addChangeTolist(stats, field, precision) {
    // asumes the stats are sorted for recent to oldest
    const statsCopy = _.cloneDeep(stats);
    for (let i = stats.length - 1; i >= 0; i--) {
      if (i === statsCopy.length - 1) {
        statsCopy[i].change = 0;
      } else {
        statsCopy[i].change = _.round(
          statsCopy[i][field] - statsCopy[i + 1][field],
          precision
        );
      }
    }
    return statsCopy;
  }

  render() {
    const { hoveredDate, unitSelected: unit, statsData } = this.state;
    const { dateFrom, dateTo } = this.props;
    const unitToLower = _.toLower(unit);
    const unitCapitalized = _.capitalize(unit);
    const avg = this.calculateAvg(statsData, this.field, this.precision);
    const roundedList = this.roundStats(statsData, this.field, this.precision);
    const headerRight = {
      name: "Average",
      value: avg ? `${avg}${unitToLower}` : 0,
    };

    const [chartData, labels] = [
      readyChartData({ stats: roundedList, field: this.field }),
      getLabels(roundedList.reverse()),
    ];

    const colors = ["rgba(91, 197, 0, 1)"];
    const start = roundedList[0] ? roundedList[0][this.field] : 0;
    const end = roundedList[roundedList.length - 1]
      ? roundedList[roundedList.length - 1][this.field]
      : 0;
    const change = _.round(end - start, this.precision);

    // RPE List data
    const statsList = this.addChangeTolist(
      mapUtcToDatePicker({
        stats: statsData,
        fieldToConvert: "utcDate",
        newField: "date",
      }),
      this.field,
      this.precision
    );

    const columnsData = [
      {
        name: "Date",
        field: "date",
        columnClass: "date",
        itemClass: "date",
      },
      {
        name: unit,
        field: this.field,
        columnClass: "bodyweight",
        itemClass: "bodyweight",
      },
      {
        name: "",
        field: "change",
        columnClass: "change",
        itemClass: "change",
        unit: unit,
      },
    ];

    const chartOptions = {
      showLine: true,
      ticks: [],
      axisY: {
        offset: 50,
        labelOffset: {
          x: 5,
          y: 5,
        },
        labelInterpolationFnc: (value) => value.toFixed(1),
      },
    };

    return (
      <div className="Readiness-Rate-Main bodyweight">
        {statsList.length ? (
          <React.Fragment>
            <div className="flex-wrapper bodyweight">
              <GraphComponent
                bodyMeasurement={true}
                dateTo={dateTo}
                dateFrom={dateFrom}
                series={chartData}
                labels={labels}
                chartName={unit}
                displayLeyend={false}
                colors={colors}
                areaOpacity={0.3}
                updateHoveredStat={this.updateHoveredStat}
                chartOptions={chartOptions}
                headerRight={headerRight}
                tooltipUnit={unitCapitalized}
              />
            </div>
            <div className="bar-container">
              <div>
                <span>
                  <b>
                    {start} {unitCapitalized}
                  </b>
                </span>

                <h4>Start</h4>
              </div>
              <div data-middle="true">
                <span>
                  <b>
                    {end} {unitCapitalized}
                  </b>
                </span>

                <h4>End</h4>
              </div>
              <div>
                <span>
                  <b>
                    {change} {unitCapitalized}
                  </b>
                </span>
                <h4>Change</h4>
              </div>
            </div>

            <div className="flex-wrapper">
              <RPEList
                dateFrom={dateFrom}
                dateTo={dateTo}
                list={statsList}
                columnsData={columnsData}
                customClass={"bodyweight"}
                highlightedElem={hoveredDate}
                secondary
              />
            </div>
          </React.Fragment>
        ) : (
          <div className="empty-state h-full">
            <span className="font-bold">No data to show</span>
          </div>
        )}
      </div>
    );
  }
}

BodyweightSummary.contextTypes = { locals: PropTypes.object };

export default BodyweightSummary;
